import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { getCurrentEmpresaIdSync } from '../utils/empresaUtils';
import { generateCatalogSlug } from '../utils/slugify';

export interface Categoria {
  id: string;
  nombre: string;
  descripcion?: string;
  color?: string;
  icono?: string;
  /** Segmento de URL: /:slug/categoria/{slug} */
  slug?: string;
  activa: boolean;
  createdAt: Date;
  updatedAt: Date;
  empresaId?: string; // Make optional for default categories
}

// Helper function to convert Supabase categoria to local format
const convertSupabaseToLocal = (c: any): Categoria => ({
  id: c.id,
  nombre: c.nombre,
  descripcion: c.descripcion || '',
  color: c.color || '#3B82F6',
  icono: c.icono || '', // Ahora puede ser una URL de imagen o vacío
  slug: c.slug || undefined,
  activa: c.activa !== false,
  createdAt: new Date(c.created_at),
  updatedAt: new Date(c.updated_at),
  empresaId: c.empresa_id
});

// Helper function to convert local categoria to Supabase format
const convertLocalToSupabase = (c: Partial<Categoria>, empresaId: string) => {
  const out: Record<string, unknown> = { empresa_id: empresaId, orden: 0 };
  if (c.nombre !== undefined) out.nombre = c.nombre || '';
  if (c.descripcion !== undefined) out.descripcion = c.descripcion || '';
  if (c.color !== undefined) out.color = c.color || '#3B82F6';
  if (c.icono !== undefined) out.icono = c.icono || '';
  if (c.activa !== undefined) out.activa = c.activa !== false;
  const nombre = c.nombre !== undefined ? c.nombre || '' : '';
  if (c.slug !== undefined && String(c.slug).trim()) {
    out.slug = generateCatalogSlug(String(c.slug).trim());
  } else if (nombre.trim()) {
    out.slug = generateCatalogSlug(nombre);
  }
  return out;
};

interface CategoriasState {
  categorias: Categoria[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchCategorias: () => Promise<void>;
  crearCategoria: (categoria: Omit<Categoria, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  actualizarCategoria: (id: string, categoria: Partial<Categoria>) => Promise<void>;
  eliminarCategoria: (id: string) => Promise<void>;
  toggleCategoriaActiva: (id: string) => Promise<void>;
  clearError: () => void;
}


export const useCategoriasStore = create<CategoriasState>((set, get) => ({
  categorias: [],
  isLoading: false,
  error: null,

  fetchCategorias: async () => {
    set({ isLoading: true, error: null });
    try {
      console.log('🔍 Fetching categorias...');
      
      // Get current user from auth store
      const { useAuthStore } = await import('./useAuthStore');
      const authStore = useAuthStore.getState();
      const currentUser = authStore.usuario;
      
      if (!currentUser || !getCurrentEmpresaIdSync() || getCurrentEmpresaIdSync() === 'sin-empresa') {
        console.log('❌ No authenticated user or empresa ID');
        set({ categorias: [], isLoading: false });
        return;
      }
      
      console.log('🏢 Fetching categorias for empresa ID:', getCurrentEmpresaIdSync());
      
      // Fetch from Supabase
      const { data: supabaseCategorias, error: fetchError } = await supabase
        .from('categorias')
        .select('*')
        .eq('empresa_id', getCurrentEmpresaIdSync())
        .order('orden', { ascending: true, nullsFirst: false });

      if (fetchError) {
        console.error('🔍 FETCH ERROR DETAILS:', {
          message: fetchError.message,
          details: fetchError.details,
          hint: fetchError.hint,
          code: fetchError.code
        });
        throw fetchError;
      }
      
      console.log('✅ Categorias loaded:', (supabaseCategorias || []).length);
      
      // Convert to local format
      const categoriasLocales = (supabaseCategorias || []).map(convertSupabaseToLocal);
      
      set({ categorias: categoriasLocales, isLoading: false, error: null });
      
    } catch (error) {
      console.error('❌ Error fetching categorias:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Error al cargar categorías desde Supabase',
        isLoading: false 
      });
    }
  },

  crearCategoria: async (nuevaCategoria) => {
    set({ isLoading: true, error: null });
    try {
      console.log('🔄 Creating categoria:', nuevaCategoria);
      
      // Get current user from auth store
      const { useAuthStore } = await import('./useAuthStore');
      const authStore = useAuthStore.getState();
      const currentUser = authStore.usuario;
      
      const empresaId = getCurrentEmpresaIdSync();
      if (!currentUser || !empresaId || empresaId === 'sin-empresa') {
        throw new Error('Usuario no tiene empresa asignada');
      }

      console.log('👤 Creating categoria for user empresa:', empresaId);

      // Convert to Supabase format
      const supabaseCategoria = convertLocalToSupabase(nuevaCategoria, empresaId);
      
      console.log('🔍 CATEGORIA TO CREATE:', supabaseCategoria);
      
      // Create in Supabase
      const { data: createdCategoria, error: createError } = await supabase
        .from('categorias')
        .insert(supabaseCategoria)
        .select()
        .single();

      if (createError) {
        console.error('🔍 SUPABASE CREATE ERROR DETAILS:', {
          message: createError.message,
          details: createError.details,
          hint: createError.hint,
          code: createError.code
        });
        throw createError;
      }
      
      console.log('✅ Categoria creada en Supabase:', createdCategoria);
      
      // Convert back to local format
      const categoriaLocal = convertSupabaseToLocal(createdCategoria);
      
      // Add to local state
      const updatedCategorias = [...get().categorias, categoriaLocal];
      set(_state => ({
        categorias: updatedCategorias,
        isLoading: false
      }));

      console.log('✅ Categoria creada exitosamente:', categoriaLocal);
      
    } catch (error) {
      console.error('❌ Error creating categoria:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Error al crear categoría en Supabase',
        isLoading: false 
      });
    }
  },

  actualizarCategoria: async (id, datosActualizados) => {
    set({ isLoading: true, error: null });
    try {
      console.log('🔄 Updating categoria:', id, datosActualizados);
      
      // Get current user from auth store
      const { useAuthStore } = await import('./useAuthStore');
      const authStore = useAuthStore.getState();
      const currentUser = authStore.usuario;

      const empresaId = getCurrentEmpresaIdSync();
      if (!currentUser || !empresaId || empresaId === 'sin-empresa') {
        throw new Error('Usuario no tiene empresa asignada');
      }

      // Convert to Supabase format
      const supabaseUpdates = convertLocalToSupabase(datosActualizados, empresaId);
      
      console.log('🔍 CATEGORIA UPDATES:', supabaseUpdates);
      
      // Update in Supabase
      const { data: updatedCategoria, error: updateError } = await supabase
        .from('categorias')
        .update(supabaseUpdates)
        .eq('id', id)
        .select()
        .single();

      if (updateError) {
        console.error('🔍 SUPABASE UPDATE ERROR DETAILS:', {
          message: updateError.message,
          details: updateError.details,
          hint: updateError.hint,
          code: updateError.code
        });
        throw updateError;
      }
      
      console.log('✅ Categoria actualizada en Supabase:', updatedCategoria);
      
      // Convert back to local format
      const categoriaLocal = convertSupabaseToLocal(updatedCategoria);
      
      // Update local state
      const updatedCategorias = get().categorias.map(c => 
        c.id === id ? categoriaLocal : c
      );
      
      set(_state => ({
        categorias: updatedCategorias,
        isLoading: false
      }));

      console.log('✅ Categoria actualizada exitosamente:', id);
    } catch (error) {
      console.error('❌ Error updating categoria:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Error al actualizar categoría en Supabase',
        isLoading: false 
      });
    }
  },

  eliminarCategoria: async (id) => {
    set({ isLoading: true, error: null });
    try {
      console.log('🔄 Deleting categoria:', id);
      
      // Delete from Supabase
      const { error: deleteError } = await supabase
        .from('categorias')
        .delete()
        .eq('id', id);

      if (deleteError) {
        console.error('🔍 SUPABASE DELETE ERROR DETAILS:', {
          message: deleteError.message,
          details: deleteError.details,
          hint: deleteError.hint,
          code: deleteError.code
        });
        throw deleteError;
      }
      
      console.log('✅ Categoria eliminada de Supabase:', id);
      
      // Remove from local state
      const updatedCategorias = get().categorias.filter(c => c.id !== id);
      
      set(_state => ({
        categorias: updatedCategorias,
        isLoading: false
      }));

      console.log('✅ Categoria eliminada exitosamente:', id);
    } catch (error) {
      console.error('❌ Error deleting categoria:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Error al eliminar categoría en Supabase',
        isLoading: false 
      });
    }
  },

  toggleCategoriaActiva: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const categoria = get().categorias.find(c => c.id === id);
      if (!categoria) {
        throw new Error('Categoría no encontrada');
      }
      
      // Update in Supabase
      const { data: updatedCategoria, error: updateError } = await supabase
        .from('categorias')
        .update({ activa: !categoria.activa })
        .eq('id', id)
        .select()
        .single();

      if (updateError) {
        console.error('🔍 SUPABASE TOGGLE ERROR DETAILS:', {
          message: updateError.message,
          details: updateError.details,
          hint: updateError.hint,
          code: updateError.code
        });
        throw updateError;
      }
      
      // Convert back to local format
      const categoriaLocal = convertSupabaseToLocal(updatedCategoria);
      
      // Update local state
      const updatedCategorias = get().categorias.map(c => 
        c.id === id ? categoriaLocal : c
      );
      
      set(_state => ({
        categorias: updatedCategorias,
        isLoading: false
      }));

      console.log('✅ Categoria estado cambiado exitosamente:', id);
    } catch (error) {
      console.error('❌ Error toggling categoria:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Error al cambiar estado de categoría en Supabase',
        isLoading: false 
      });
    }
  },

  clearError: () => set({ error: null })
}));

